import React, { useState, useMemo, memo } from "react";
import {
  Backdrop,
  Rating,
  Box,
  Link as MuiLink,
  Typography,
  Tooltip,
  Stack,
} from "@mui/material";
import { Link } from "react-router-dom";
import moment from "moment";

import DurationFilter, {
  DURATION_OPTIONS,
  IDurationOption,
} from "../../common/DurationFilter";
import PieChart, { IPieChartData } from "../../common/PieChart";
import SortableTable, {
  ISortableTableProps,
  TSortableColumn,
} from "../../common/SortableTable";
import Tag from "../../common/Tag";
import CircularProgress from "../../../../components/CircularProgress";

import { useAppSelector } from "../../../../store/hooks";
import {
  capitalize,
  formatCurrency,
  formatNumber,
  formatPercent,
  isNumber,
} from "../../../../helpers";
import { selectBuyBoxAnalysis } from "../../../../features/productAnalyzer/productAnalyzer.selector";
import { TBuyBoxAnalysis } from "../../../../features/productAnalyzer/productAnalyzer.types";
import { selectProductAnalyzerSettings } from "../../../../features/settings/settings.selector";
import { FONT_COLORS } from "../../../../constants";
// import { selectIsFreePlan } from "../../../../features/pricingPlans/pricingPlans.selector";
// import LockOverlay from "../../../../components/LockOverlay";
import useProductAnalyzerParams from "../../../../hooks/useProductAnalyzerParams";
import { getProductDetails } from "../../../../features/productAnalyzer/productAnalyzer.thunk";

export type TSortColumn = {
  column: string;
  order: "asc" | "desc";
};

export type TBuyBoxAnalysisTableRow = TBuyBoxAnalysis & {
  sellerName: string;
  rating: number;
  ratingCount: number;
};

const TABLE_COLUMNS: TSortableColumn<TBuyBoxAnalysisTableRow>[] = [
  {
    header: "Seller",
    accessorKey: "sellerName",
    align: "left",
    width: "120px",
    html: (data: TBuyBoxAnalysisTableRow) => {
      const isAmz = data.type === "AMZ";
      const tooltip = isAmz ? (
        <Stack>
          <Typography variant="body1">Amazon.com</Typography>
          <Typography variant="body2">Fulfilled by Amazon (Prime)</Typography>
        </Stack>
      ) : (
        <Stack>
          <Typography variant="body1">{data.sellerName || "-"}</Typography>
          {data.sellerId !== "-1" && data.sellerId !== "-2" && (
            <Box
              display={"flex"}
              alignItems={"center"}
              justifyContent={"flex-start"}
            >
              {!!data.ratingCount && data.ratingCount > 0 && (
                <Rating
                  value={data.rating || 0}
                  readOnly
                  precision={0.1}
                  sx={{ fontSize: "12px" }}
                />
              )}
              <Typography variant="body1">
                {data.ratingCount && data.ratingCount > 0
                  ? `(${Intl.NumberFormat("en-US", {
                      useGrouping: true,
                    }).format(data.ratingCount)})`
                  : "No reviews yet"}
              </Typography>
            </Box>
          )}
          {data.type && (
            <Typography variant="body2">
              {data.type === "FBA"
                ? "Fulfilled by Amazon (Prime)"
                : data.type === "FBM"
                  ? "Fulfilled by Merchant"
                  : ""}
            </Typography>
          )}
        </Stack>
      );

      return (
        <Box
          sx={{
            width: "100%",
          }}
        >
          {data.sellerId !== "-1" &&
            data.sellerId !== "-2" &&
            data.type !== "AMZ" && (
              <Tooltip title={tooltip} arrow>
                <MuiLink
                  component={Link}
                  to={data.link}
                  target="_blank"
                  sx={{
                    textDecoration: "none",
                    color: "#000000",
                    display: "block",
                    width: "100%",
                    "&:hover": {
                      textDecoration: "underline",
                      color: FONT_COLORS.info,
                    },
                  }}
                >
                  {data.sellerName || "-"}
                </MuiLink>
              </Tooltip>
            )}
          {data.sellerId === "-1" && (
            <Tooltip title="This seller's offers are suppressed" arrow>
              <Box>
                SUPPRESSED
              </Box>
            </Tooltip>
          )}
          {data.sellerId === "-2" && (
            <Tooltip title="This seller is currently out of stock" arrow>
              <Box>
                OUT OF STOCK
              </Box>
            </Tooltip>
          )}
          {data.type === "AMZ" && (
            <Tooltip title={tooltip} arrow>
              <Box>
                {data.sellerName}
              </Box>
            </Tooltip>
          )}
          {data.rating > 0 && (
            <Box
              display="flex"
              alignItems="center"
              gap="4px"
              sx={{ mt: "4px" }}
            >
              <Rating
                value={data.rating}
                readOnly
                precision={0.1}
                sx={{ fontSize: "12px" }}
              />
              {data.ratingCount > 0 && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "11px",
                    color: "#666666",
                    lineHeight: "16px",
                  }}
                >
                  (
                  {Intl.NumberFormat("en-US", {
                    useGrouping: true,
                  }).format(data.ratingCount)}
                  )
                </Typography>
              )}
            </Box>
          )}
        </Box>
      );
    },
    wrap: true,
  },
  {
    header: "Type",
    accessorKey: "type",
    align: "center",
    width: "50px",
    html: (data: TBuyBoxAnalysisTableRow) => {
      if (!data.type || data.type === "-") {
        return <Box>-</Box>;
      }

      const variantMap: Record<string, "success" | "info" | "warning"> = {
        FBA: "success",
        FBM: "info",
        AMZ: "warning",
      };

      const variant = variantMap[data.type] || "success";

      return (
        <Box display="flex" justifyContent="center" alignItems="center">
          <Tag title={data.type} variant={variant} badgeStyle />
        </Box>
      );
    },
  },
  {
    header: "Avg Price",
    accessorKey: "avgPrice",
    width: "80px",
    formatter: (data: TBuyBoxAnalysisTableRow) =>
      data.avgPrice ? formatCurrency(data.avgPrice) : "-",
  },
  {
    header: "Won",
    accessorKey: "won",
    width: "50px",
    formatter: (data: TBuyBoxAnalysisTableRow) =>
      isNumber(data.won) && data.won < 1
        ? "<1%"
        : formatPercent(Math.round(data.won), 0),
  },
  {
    header: "Last Won",
    accessorKey: "lastWon",
    width: "80px",
    wrap: true,
    html: (data: TBuyBoxAnalysisTableRow) => {
      const relativeTime = capitalize(moment(data.lastWon).fromNow());
      const exactDate = moment(data.lastWon).format("MMMM D, YYYY [at] h:mm A");
      return (
        <Tooltip title={`${exactDate} (${relativeTime})`} arrow>
          <Box sx={{ cursor: "pointer" }}>{relativeTime}</Box>
        </Tooltip>
      );
    },
  },
  {
    header: "Stock",
    accessorKey: "stock",
    width: "50px",
    formatter: (data: TBuyBoxAnalysisTableRow) =>
      data.stock === 0
        ? "-"
        : formatNumber(data.stock, { useAbbreviation: false }),
  },
];

const BuyBoxAnalysis: React.FC = () => {
  const { asin, lookupId } = useProductAnalyzerParams();
  const { defaultBbAnalysisTimeFrame } = useAppSelector(
    selectProductAnalyzerSettings,
  );
  // const isFreePlan = useAppSelector(selectIsFreePlan);
  const [duration, setDuration] = useState<string>(
    defaultBbAnalysisTimeFrame || DURATION_OPTIONS[1].id,
  );
  const { buyBoxAnalysis = [], loadingBuyBox } = useAppSelector((state) =>
    selectBuyBoxAnalysis(
      state,
      DURATION_OPTIONS.find((option) => option.id === duration)?.days ?? 0,
    ),
  );

  const handleDurationChange = (option: IDurationOption) => {
    if (duration !== option.id) {
      setDuration(option.id);
    }
  };

  // Helper functions
  const getCompetitionLevel = (sellerCount: number): string => {
    if (sellerCount <= 2) return "Low";
    if (sellerCount <= 4) return "Medium";
    return "High";
  };

  const getOpportunityLevel = (
    dominantWon: number,
  ): { level: string; emoji: string } => {
    if (dominantWon > 80) return { level: "Low", emoji: "🔴" };
    if (dominantWon > 50) return { level: "Medium", emoji: "🟡" };
    return { level: "High", emoji: "🟢" };
  };

  // Insights calculations
  const insights = useMemo(() => {
    if (!buyBoxAnalysis || !buyBoxAnalysis.length) return null;

    const validSellers = buyBoxAnalysis.filter(
      (s) => s.sellerId !== "-1" && s.sellerId !== "-2",
    );
    const dominantSeller = [...buyBoxAnalysis].sort((a, b) => b.won - a.won)[0];
    const priceLeader = [...validSellers]
      .filter((s) => s.avgPrice && s.avgPrice > 0)
      .sort((a, b) => (a.avgPrice || 0) - (b.avgPrice || 0))[0];

    const competition = getCompetitionLevel(validSellers.length);
    const opportunity = getOpportunityLevel(dominantSeller.won);

    return {
      dominantSeller: {
        name: dominantSeller.sellerName || "Unknown",
        won: dominantSeller.won,
        avgPrice: dominantSeller.avgPrice,
        type: dominantSeller.type,
      },
      priceLeader: {
        name: priceLeader?.sellerName || "Unknown",
        won: priceLeader?.won || 0,
        avgPrice: priceLeader?.avgPrice || 0,
        type: priceLeader?.type,
      },
      competition: {
        level: competition,
        count: validSellers.length,
      },
      opportunity: {
        level: opportunity.level,
        emoji: opportunity.emoji,
      },
    };
  }, [buyBoxAnalysis]);

  // Chart Data
  const chartsData = useMemo<IPieChartData[]>(() => {
    if (!buyBoxAnalysis || !buyBoxAnalysis.length) return [];

    return buyBoxAnalysis.map((item: TBuyBoxAnalysisTableRow) => ({
      name:
        item.sellerId === "-1"
          ? "SUPPRESSED"
          : item.sellerId === "-2"
            ? "OUT OF STOCK"
            : item.sellerName || "-",
      y: item.won,
      rating: item.rating ?? 0,
      ratingCount: item.ratingCount ?? 0,
      avgPrice: item.avgPrice ? formatCurrency(item.avgPrice) : "-",
      won: item.won < 1 ? "<1%" : Math.round(item.won) + "%",
      lastWon: capitalize(moment(item.lastWon).fromNow()),
      type: item.type,
      color: item.type === "AMZ" ? "#FF9900" : undefined,
    }));
  }, [buyBoxAnalysis, duration]);

  return (
    <Box>
      <Box sx={{ padding: "4px", borderBottom: "1px solid #181D1F26" }}>
        <DurationFilter
          value={duration}
          onChange={handleDurationChange}
          withRange
        />
      </Box>

      {/* Summary Cards */}
      {insights && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: "4px",
            paddingY: "4px",
            paddingX: "4px",
            borderBottom: "1px solid #D4D6D8",
          }}
        >
          <Box
            sx={{
              height: "50px",
              backgroundColor: "#F9FAFB",
              borderRadius: "6px",
              border: "1px solid #C9C9C9",
              padding: "12px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
              boxSizing: "border-box",
            }}
          >
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 500,
                lineHeight: "18px",
                color: "#6B7280",
                textTransform: "uppercase",
                textAlign: "center",
              }}
            >
              DOMINANT SELLER
            </Typography>
            <Tooltip
              title={`${insights.dominantSeller.name} (${insights.dominantSeller.won < 1 ? "<1%" : formatPercent(Math.round(insights.dominantSeller.won), 1)})`}
              arrow
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 700,
                  color: "#000000",
                  lineHeight: "18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                  width: "100%",
                  minWidth: 0,
                  cursor: "pointer",
                }}
              >
                <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                  {insights.dominantSeller.name}
                </Box>
                ({insights.dominantSeller.won < 1 ? "<1%" : formatPercent(Math.round(insights.dominantSeller.won), 1)})
              </Typography>
            </Tooltip>
          </Box>

          <Box
            sx={{
              height: "50px",
              backgroundColor: "#F9FAFB",
              borderRadius: "6px",
              border: "1px solid #C9C9C9",
              padding: "12px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
              boxSizing: "border-box",
            }}
          >
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 500,
                lineHeight: "18px",
                color: "#6B7280",
                textTransform: "uppercase",
                textAlign: "center",
              }}
            >
              PRICE LEADER
            </Typography>
            <Tooltip
              title={`${insights.priceLeader.name} (${insights.priceLeader.won < 1 ? "<1%" : formatPercent(Math.round(insights.priceLeader.won), 0)})`}
              arrow
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 700,
                  color: "#000000",
                  lineHeight: "18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                  width: "100%",
                  minWidth: 0,
                }}
              >
                <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                  {insights.priceLeader.name}
                </Box>
                ({insights.priceLeader.won < 1 ? "<1%" : formatPercent(Math.round(insights.priceLeader.won), 0)})
              </Typography>
            </Tooltip>
          </Box>

          <Box
            sx={{
              height: "50px",
              backgroundColor: "#F9FAFB",
              borderRadius: "6px",
              border: "1px solid #C9C9C9",
              padding: "12px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
              boxSizing: "border-box",
            }}
          >
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 500,
                lineHeight: "18px",
                color: "#6B7280",
                textTransform: "uppercase",
                textAlign: "center",
              }}
            >
              COMPETITION
            </Typography>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 700,
                color: "#000000",
                lineHeight: "18px",
                textAlign: "center",
              }}
            >
              {insights.competition.level} ({insights.competition.count}{" "}
              sellers)
            </Typography>
          </Box>

          <Box
            sx={{
              height: "50px",
              backgroundColor: "#F9FAFB",
              borderRadius: "6px",
              border: "1px solid #C9C9C9",
              padding: "12px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
              boxSizing: "border-box",
            }}
          >
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 500,
                lineHeight: "18px",
                color: "#6B7280",
                textTransform: "uppercase",
                textAlign: "center",
              }}
            >
              OPPORTUNITY
            </Typography>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 700,
                color: "#000000",
                lineHeight: "18px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
              }}
            >
              {insights.opportunity.level}
              <span>{insights.opportunity.emoji}</span>
            </Typography>
          </Box>
        </Box>
      )}

      <Box position={"relative"}>
        <Box width={"100%"} display={"block"} position={"relative"}>
          <PieChart data={chartsData} />
          {/* {isFreePlan && (
            <LockOverlay
              title="Buy Box Analysis"
              asin={asin}
              lookupId={lookupId}
            />
          )} */}
        </Box>

        <SortableTable
          columns={TABLE_COLUMNS}
          data={buyBoxAnalysis || []}
          defaultSort={{ accessorKey: "won", order: "desc" }}
          noBorderBottom
          // lock={isFreePlan}
          asin={asin}
          lookupId={lookupId}
          title="Buy Box Analysis"
          // numberOfRowsToShow={isFreePlan ? 1 : 0}
        />

        {loadingBuyBox && (
          <Backdrop open={true} sx={{ position: "absolute", zIndex: 999 }}>
            <CircularProgress size="md" />
          </Backdrop>
        )}
      </Box>
    </Box>
  );
};

export default memo(BuyBoxAnalysis);
